// Carrying a font into a rasterized SVG.
//
// An SVG handed to an <img> is rendered as its own document: it cannot see the page's fonts and it
// cannot fetch anything, so text in it comes out in the rasterizer's fallback face no matter what
// the page loaded. The only way through is to inline the font bytes in the markup itself.
//
// This is why the cloud's PNG looked wrong even with the font demonstrably loaded on the page: the
// layout measured the real face (canvas text metrics do see page fonts), while the picture drew a
// different one.

import { resolveFontData } from '@aytm/app/stats/page/question-views/components/word-cloud/hooks/useCloudLayoutWorker'

const toBase64 = buffer => {
  const bytes = new Uint8Array(buffer)
  let binary = ''

  // Chunked: spreading a whole font file into String.fromCharCode blows the argument limit.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000))
  }

  return btoa(binary)
}

/**
 * The `@font-face` rule that makes `fontFamily` renderable inside a standalone SVG.
 *
 * @param {string} fontFamily
 * @returns {Promise<string|null>} the CSS, or null when the font's bytes cannot be reached
 */
export const buildFontFaceCss = async fontFamily => {
  if (!fontFamily) return null

  const data = await resolveFontData(fontFamily).catch(() => null)
  if (!data) return null

  return `@font-face { font-family: '${fontFamily}'; src: url(data:font/woff2;base64,${toBase64(data)}); }`
}

/**
 * Inline the font an SVG draws with, so a rasterizer that cannot reach the page still sees it.
 * A font that cannot be resolved is left alone — a fallback face beats no picture.
 *
 * @param {SVGElement} svg - the element about to be serialized (a clone, not the live node)
 * @param {string} fontFamily
 */
export const embedFont = async (svg, fontFamily) => {
  const css = await buildFontFaceCss(fontFamily)
  if (!css) return

  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
  style.textContent = css
  svg.insertBefore(style, svg.firstChild)
}

export default embedFont
